import { LaunchAttempt, LaunchReport, subscribeMobileWebDiagnostics } from './mobileWebDiagnostics';

const ENDPOINT = '/intersect/mobile-web-diagnostics';
const MAX_PAYLOAD_BYTES = 24000;
const GPU_FEATURES = new Set(['texture-compression-astc', 'texture-compression-etc2', 'texture-compression-bc',
  'shader-f16', 'float32-filterable', 'depth-clip-control', 'timestamp-query']);

/** Normalize before sending: omit raw user agents, URLs, errors and all auth data. */
export function mobileWebTelemetryPayload(attempt: LaunchAttempt, source: 'current' | 'recovered', recoveredAfterLaunchId?: string) {
  const ua = attempt.browser.userAgent;
  const deviceFamily = /iPhone|iPod/i.test(ua) ? 'iphone' : /iPad/i.test(ua) ? 'ipad' :
    /Android/i.test(ua) ? 'android' : /Macintosh/i.test(ua) ? 'mac' : /Windows/i.test(ua) ? 'windows' : 'other';
  const browserFamily = /CriOS/i.test(ua) ? 'chrome-ios' : /FxiOS/i.test(ua) ? 'firefox-ios' :
    /EdgiOS/i.test(ua) ? 'edge-ios' : /Chrome/i.test(ua) ? 'chrome' : /Safari/i.test(ua) ? 'safari' : 'other';
  const osVersion = ua.match(/(?:CPU(?: iPhone)? OS|iPhone OS) (\d+(?:_\d+)*)/)?.[1].replace(/_/g, '.') || '';
  return { source, ...(recoveredAfterLaunchId ? { recoveredAfterLaunchId } : {}), attempt: {
    schemaVersion: attempt.schemaVersion,
    launchId: attempt.launchId,
    startedAt: attempt.startedAt,
    voiceDisabled: attempt.voiceDisabled,
    navigationType: attempt.navigationType || '',
    browser: { deviceFamily, browserFamily, osVersion, viewport: attempt.browser.viewport,
      devicePixelRatio: attempt.browser.devicePixelRatio || 1, secureContext: attempt.browser.secureContext === true },
    ...(attempt.graphics ? { graphics: { ...attempt.graphics,
      features: attempt.graphics.features.filter(feature => GPU_FEATURES.has(feature)) } } : {}),
    ...(attempt.runtime ? { runtime: attempt.runtime } : {}),
    milestones: attempt.milestones,
  } };
}

/** One request in flight, coalesced checkpoints, bounded recovery and retry. */
export function startMobileWebTelemetry(accessToken: string, apiUrl = process.env.REACT_APP_API_URL): () => void {
  if (!apiUrl) return () => {};
  let stopped = false;
  let blocked = false;
  let sending = false;
  let retryDelay = 1000;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let scheduled: ReturnType<typeof setTimeout> | undefined;
  const pending = new Map<string, ReturnType<typeof mobileWebTelemetryPayload>>();
  const recovered = new Set<string>();

  const flush = async () => {
    if (stopped || blocked || sending || retryTimer || !pending.size) return;
    const [id, payload] = pending.entries().next().value!;
    const body = JSON.stringify(payload);
    pending.delete(id);
    if (new TextEncoder().encode(body).byteLength > MAX_PAYLOAD_BYTES) return;
    sending = true;
    try {
      const response = await fetch(`${apiUrl.replace(/\/$/, '')}${ENDPOINT}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body, keepalive: true,
      });
      if (!response.ok) {
        // Backend not deployed or auth/schema rejected: do not flood it on every sample.
        if ([401, 403, 404, 405].includes(response.status)) blocked = true;
        else if (response.status === 429 || response.status >= 500) throw new Error('retry');
      }
      retryDelay = 1000;
    } catch (_) {
      if (!stopped) {
        // Preserve a newer snapshot of this attempt if one arrived during the request.
        if (!pending.has(id)) pending.set(id, payload);
        retryTimer = setTimeout(() => { retryTimer = undefined; void flush(); }, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      }
    } finally {
      sending = false;
      if (!stopped && !blocked && !retryTimer) void flush();
    }
  };

  const queue = (snapshot: LaunchReport) => {
    if (stopped || blocked) return;
    if (snapshot.previousLaunch && !recovered.has(snapshot.previousLaunch.launchId)) {
      const previous = snapshot.previousLaunch;
      recovered.add(previous.launchId);
      pending.set(`recovered:${previous.launchId}`, mobileWebTelemetryPayload(previous, 'recovered', snapshot.launchId));
    }
    pending.set(`current:${snapshot.launchId}`, mobileWebTelemetryPayload(snapshot, 'current'));
    // Keep only this launch and its predecessor, even if components remount.
    while (pending.size > 2) pending.delete(pending.keys().next().value!);
    const stage = snapshot.milestones[snapshot.milestones.length - 1]?.stage;
    if (stage === 'hidden' || stage === 'page-hidden' || stage === 'player-start' || stage === 'media-requested') {
      if (scheduled) clearTimeout(scheduled);
      scheduled = undefined;
      void flush();
    } else if (!scheduled) {
      scheduled = setTimeout(() => { scheduled = undefined; void flush(); }, 100);
    }
  };
  const unsubscribe = subscribeMobileWebDiagnostics(queue);
  return () => {
    stopped = true;
    unsubscribe();
    if (scheduled) clearTimeout(scheduled);
    if (retryTimer) clearTimeout(retryTimer);
    pending.clear();
  };
}
